import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { useMember } from '@/lib/use-session';
import { resourcesKeys } from '@/lib/queries/resources';
import { adminKeys } from '@/lib/queries/admin';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { EditResourceForm } from './-components/EditResourceForm';

export const Route = createFileRoute('/_authed/resources/$id/edit/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: EditResourcePage,
});

function EditResourcePage() {
  const { id } = Route.useParams();
  const me = useMember();
  const isAdmin = me?.platformRole === 'admin';

  // Edit is admin-only, and the admin detail endpoint (unlike the public
  // one) returns unpublished drafts and moderation-held resources — which an
  // admin must be able to open in the editor. Gate the fetch on isAdmin so
  // non-admins never hit it.
  const resourceQuery = useQuery({
    ...adminKeys.resourceDetail(id),
    enabled: isAdmin,
    retry: false,
  });
  const topicsQuery = useQuery(resourcesKeys.topics());

  if (me === null) return null;
  if (!isAdmin) return <NotFoundPage />;
  if (resourceQuery.isLoading || topicsQuery.isLoading) return null;
  if (isNotFoundApiError(resourceQuery.error)) return <NotFoundPage />;
  if (resourceQuery.isError || !resourceQuery.data) throw notFound();

  const resource = resourceQuery.data;

  const availableTopics = topicsQuery.data ?? [];
  const slugToId = new Map(availableTopics.map((t) => [t.slug, t.id]));
  const currentTopicIds = resource.tags
    .map((t) => slugToId.get(t.slug))
    .filter((id): id is string => id !== undefined);

  return (
    <EditResourceForm
      resourceId={resource.id}
      initial={{
        title: resource.title,
        lede: resource.lede,
        bodyMarkdown: resource.bodyMarkdown,
        coverImageUrl: resource.coverImageUrl,
        topicIds: currentTopicIds,
        attachments: resource.attachments,
        scope: resource.scope,
        showAuthor: resource.showAuthor,
        kind: resource.kind,
        contentLayer: resource.contentLayer,
      }}
      availableTopics={availableTopics}
    />
  );
}
